import { AppstoreAddOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type PorschePaymentModuleSpecsFragment } from '../../../../api/fragments/PorschePaymentModuleSpecs';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import useDeleteModule from '../useDeleteModule';
import { usePorschePaymentSettingsDrawer } from './PorschePaymentSettingsDrawer';
import { useUpdatePorschePaymentModuleDrawer } from './UpdatePorschePaymentModuleDrawer';

export type PorschePaymentModuleActionsProps = {
    module: PorschePaymentModuleSpecsFragment;
    permissions: Pick<PermissionValues, 'hasDeletePermission' | 'hasUpdatePermission'>;
};

const PorschePaymentModuleActions = ({ module, permissions }: PorschePaymentModuleActionsProps) => {
    const { t } = useTranslation(['porschePaymentModuleDetails', 'moduleDetails']);
    const deleteModule = useDeleteModule(module.id);
    const addPorschePaymentSettingsDrawer = usePorschePaymentSettingsDrawer(module.id);

    const initialValues = useMemo(() => pick(['displayName'], module), [module]);
    const updatePorschePaymentModuleDrawer = useUpdatePorschePaymentModuleDrawer(module.id, initialValues);
    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const items = [
        hasUpdatePermission && {
            label: t('porschePaymentModuleDetails:mainActionButtons.updateSpecs'),
            key: 'updateSpecs',
            icon: <EditOutlined />,
            onClick: updatePorschePaymentModuleDrawer.open,
        },
        hasUpdatePermission && {
            label: t('porschePaymentModuleDetails:mainActionButtons.addSetting'),
            key: 'addSetting',
            icon: <AppstoreAddOutlined />,
            onClick: addPorschePaymentSettingsDrawer.open,
        },
        hasDeletePermission && {
            label: t('porschePaymentModuleDetails:mainActionButtons.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ].filter(Boolean);

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('porschePaymentModuleDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {addPorschePaymentSettingsDrawer.render()}
            {updatePorschePaymentModuleDrawer.render()}
        </>
    );
};

export default PorschePaymentModuleActions;
